import React, { useState } from 'react';
import { authService } from '../../services/authService';
import {
    FaLock,
    FaEye,
    FaEyeSlash,
    FaTimes,
    FaCheck,
    FaCheckCircle,
    FaExclamationCircle
} from 'react-icons/fa';
import styles from './styles/ChangePasswordModal.module.css';

export default function ChangePasswordModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        try {
            // Validation
            if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
                throw new Error('Tous les champs sont requis');
            }

            if (formData.newPassword !== formData.confirmPassword) {
                throw new Error('Les nouveaux mots de passe ne correspondent pas');
            }

            if (formData.newPassword.length < 6) {
                throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères');
            }

            if (formData.currentPassword === formData.newPassword) {
                throw new Error('Le nouveau mot de passe doit être différent de l\'ancien');
            }

            setLoading(true);

            // Appeler le service pour changer le mot de passe
            await authService.changePassword(
                formData.currentPassword,
                formData.newPassword
            );

            setSuccess(true);
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });

            // Fermer la modale après 2 secondes
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setError(null);
        setSuccess(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleCancel}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerIcon}>
                            <FaLock />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>Changer le mot de passe</h2>
                            <p className={styles.modalSubtitle}>Sécurisez votre compte avec un nouveau mot de passe</p>
                        </div>
                    </div>
                    <button
                        className={styles.closeBtn}
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.modalBody}>
                    {/* Success Message */}
                    {success && (
                        <div className={styles.successMessage}>
                            <FaCheckCircle className={styles.messageIcon} />
                            <div>
                                <strong>Succès</strong>
                                <p>Mot de passe changé avec succès!</p>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className={styles.errorMessage}>
                            <FaExclamationCircle className={styles.messageIcon} />
                            <div>
                                <strong>Erreur</strong>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Mot de passe actuel */}
                        <div className={styles.formGroup}>
                            <label htmlFor="currentPassword">Mot de passe actuel *</label>
                            <div className={styles.inputWrapper}>
                                <FaLock className={styles.inputIcon} />
                                <input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    id="currentPassword"
                                    name="currentPassword"
                                    value={formData.currentPassword}
                                    onChange={handleChange}
                                    placeholder="Entrez votre mot de passe actuel"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.togglePassword}
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    disabled={loading}
                                >
                                    {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        {/* Nouveau mot de passe */}
                        <div className={styles.formGroup}>
                            <label htmlFor="newPassword">Nouveau mot de passe *</label>
                            <div className={styles.inputWrapper}>
                                <FaLock className={styles.inputIcon} />
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    placeholder="Entrez votre nouveau mot de passe"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.togglePassword}
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    disabled={loading}
                                >
                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            <small className={styles.hint}>
                                Minimum 6 caractères
                            </small>
                        </div>

                        {/* Confirmer mot de passe */}
                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
                            <div className={styles.inputWrapper}>
                                <FaLock className={styles.inputIcon} />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirmez votre nouveau mot de passe"
                                    disabled={loading}
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.togglePassword}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    disabled={loading}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className={styles.passwordStrength}>
                                <div className={styles.strengthBar}>
                                    <div
                                        className={`${styles.strengthFill} ${formData.newPassword.length >= 8 ? styles.strong :
                                            formData.newPassword.length >= 6 ? styles.medium :
                                                styles.weak
                                            }`}
                                    ></div>
                                </div>
                                <small className={styles.strengthText}>
                                    {formData.newPassword.length >= 8 ? 'Mot de passe fort' :
                                        formData.newPassword.length >= 6 ? 'Mot de passe moyen' :
                                            'Mot de passe faible'}
                                </small>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className={styles.formActions}>
                            <button
                                type="submit"
                                className={styles.submitBtn}
                                disabled={loading}
                            >
                                {loading ? 'Changement en cours...' : (
                                    <>
                                        <FaCheck /> Confirmer
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                <FaTimes /> Annuler
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
